import { useMemo, useState } from 'react';
import { HorizonHero } from '../../horizon/HorizonHero';
import { HorizonSignalGroupRow } from '../../horizon/HorizonSignals';
import { HorizonWeek } from '../../horizon/HorizonWeek';
import { useHorizonBookings } from '../../horizon/useHorizon';
import '../../../styles/horizon.css';
import { useAssistantWidgetData } from './WidgetDataContext';
import { buildHorizonWidgetModel } from './horizonWidgetModel';

function Empty({ text }: { text: string }) {
  return <p className="maitre-tile-empty">{text}</p>;
}

function useHorizonModel(requestedOutletId?: string) {
  const ctx = useAssistantWidgetData();
  const data = ctx?.horizon;
  const [focusOutletId, setFocusOutletId] = useState<string | null>(
    requestedOutletId ?? null,
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const scopeHint = focusOutletId ?? requestedOutletId ?? null;
  const model = useMemo(
    () => (data ? buildHorizonWidgetModel(data, scopeHint) : null),
    [data, scopeHint],
  );
  const bookings = useHorizonBookings(
    ctx?.restaurantId,
    model?.scopeId ?? null,
    model?.nearDays[0]?.serviceDate ?? null,
    ctx?.regionFilter ?? null,
    !!model?.scopeId,
    ctx?.shifts ?? null,
  );
  const withTiles = useMemo(
    () =>
      data
        ? buildHorizonWidgetModel(data, scopeHint, bookings.data?.days ?? null)
        : null,
    [data, scopeHint, bookings.data?.days],
  );
  return {
    ctx,
    model: withTiles,
    bookings,
    selectedDate,
    setSelectedDate,
    setFocusOutletId,
  };
}

export function HorizonWeekWidget({ outletId }: { outletId?: string }) {
  const { ctx, model, bookings, selectedDate, setSelectedDate, setFocusOutletId } =
    useHorizonModel(outletId);
  if (ctx?.horizonLoading) return <Empty text="Loading…" />;
  if (ctx?.horizon?.quality && ctx.horizon.quality.ok === false) {
    return <Empty text={ctx.horizon.quality.message || 'Forecast is not available.'} />;
  }
  if (!model) return <Empty text="No upcoming book yet." />;
  return (
    <div className="horizon hz-widget">
      <HorizonWeek
        days={model.nearDays}
        bookingDays={bookings.data?.days ?? null}
        timeZone={bookings.data?.timezone ?? null}
        focusedOutletId={model.scopeId}
        selectedDate={selectedDate}
        flaggedDates={model.flagged}
        loadingBookings={!!bookings.isLoading && !bookings.data}
        onSelect={day => setSelectedDate(day.serviceDate)}
        onFocusOutlet={id => setFocusOutletId(id)}
      />
    </div>
  );
}

export function HorizonHeroWidget({ outletId }: { outletId?: string }) {
  const { ctx, model } = useHorizonModel(outletId);
  if (ctx?.horizonLoading) return <Empty text="Loading…" />;
  if (ctx?.horizon?.quality && ctx.horizon.quality.ok === false) {
    return <Empty text={ctx.horizon.quality.message || 'Forecast is not available.'} />;
  }
  if (!model) return <Empty text="No upcoming book yet." />;
  return (
    <div className="horizon hz-widget">
      <HorizonHero headline={model.headline} tiles={model.tiles} rail={model.rail} />
    </div>
  );
}

export function HorizonSignalsWidget({ outletId }: { outletId?: string }) {
  const { ctx, model, selectedDate, setSelectedDate } = useHorizonModel(outletId);
  if (ctx?.horizonLoading) return <Empty text="Loading…" />;
  if (ctx?.horizon?.quality && ctx.horizon.quality.ok === false) {
    return <Empty text={ctx.horizon.quality.message || 'Forecast is not available.'} />;
  }
  if (!model) return <Empty text="No upcoming book yet." />;
  if (model.signalGroups.length === 0) {
    return (
      <Empty
        text={
          ctx?.horizon?.quietReasons?.[0] ||
          'Every upcoming date is tracking where comparable services usually sit.'
        }
      />
    );
  }
  return (
    <div className="horizon hz-widget">
      <ul className="hz-signals">
        {model.signalGroups.map(group => (
          <HorizonSignalGroupRow
            key={group.key}
            group={group}
            selectedDate={selectedDate}
            showOutlets={!model.scopeId}
            outletName={model.outletName}
            onPickDate={setSelectedDate}
          />
        ))}
      </ul>
    </div>
  );
}
